import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from '../../common/withRouter';
import {
  DataDefinitionList,
  Loader,
  Notification,
  Message,
  ButtonOBSOLETED
} from '../../web.common';
import AriaModal from 'react-aria-modal';
import { LayoutContext } from '../layout/siteLayout';
import PageTop from '../layout/PageTop';
import withApi from '../higherOrderComponents/withApi';
import {
  DisplayDate,
  DisplayGuid
} from '../items';
import { DetailsRightBar, Link, Divider, Action } from './DetailsRightBar';
import {
  COPENHAGEN_TIME_NOTE,
  formatCopenhagen,
  formatCourtTime
} from '../../common/copenhagenTime';
import { weekdaysReverse } from '../../enums/weekdays';

// A refusal from the change-court / cancel endpoints comes back as a plain string body.
const errorText = e => (typeof e?.payload?.responseData === 'string'
  ? e.payload.responseData
  : (e?.message || String(e)));

const dateList = dates => (dates || []).map(d => formatCopenhagen(d, { withZone: false })).join(', ');

/**
 * "Change court": moves this purchase to another court from a given day. The
 * backend offers only courts at the same facility, in the same price band and on
 * sale, each with the first day it can take the purchase (never sooner than two
 * weeks from today, so pay and play bookings are not run over). A preview (dry
 * run) shows exactly which slots get cancelled and booked before anything moves.
 */
class ChangeCourtDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      options: null,
      courtId: '',
      moveFrom: '',
      preview: null,
      busy: false,
      error: null,
      done: null
    };
  }

  async componentDidMount() {
    const { purchaseId, api: { seasonalcourts: { changeCourtOptions } } } = this.props;
    try {
      const options = await changeCourtOptions(purchaseId);
      this.setState({ options });
    }
    catch (e) {
      this.setState({ error: `Unable to load the courts: ${errorText(e)}` });
    }
  }

  selectCourt(courtId) {
    const { options } = this.state;
    const court = (options?.courts || []).find(c => c.id === courtId);
    this.setState({
      courtId,
      moveFrom: court?.firstAvailableDate || options?.earliestMoveDate || '',
      preview: null,
      error: null
    });
  }

  async run(dryRun) {
    const { purchaseId, api: { seasonalcourts: { changeCourt } } } = this.props;
    const { courtId, moveFrom } = this.state;
    this.setState({ busy: true, error: null });
    try {
      const result = await changeCourt(purchaseId, courtId, moveFrom, dryRun);
      this.setState(dryRun ? { preview: result, busy: false } : { done: result, busy: false });
    }
    catch (e) {
      this.setState({ busy: false, preview: null, error: errorText(e) });
    }
  }

  render() {
    const { onClose, onDone } = this.props;
    const { options, courtId, moveFrom, preview, busy, error, done } = this.state;
    const courts = options?.courts || [];

    if (done) {
      return (
        <div>
          <h3>Court changed</h3>
          <p>
            Moved to <b>{done.newCourtName}</b> from {done.moveFrom}.{' '}
            {done.slotsToCancel.length} slot(s) cancelled on the old court; {done.slotsToBook.length} slot(s)
            {' '}queued for booking on the new one — the result shows up on the new purchase in a few minutes.
          </p>
          <ButtonOBSOLETED
            buttonColor='primary'
            size='small'
            onClick={() => onDone(done.newSeasonalCourtPurchaseId)}
            disabled={false}
            buttonContent='Open the new purchase'
            ariaLabel='open the new purchase'
          >
            Open the new purchase
          </ButtonOBSOLETED>
        </div>
      );
    }

    return (
      <div>
        <h3>Change court</h3>
        {
          !options && !error ? <Loader hasLoaded={false}>Loading courts...</Loader> : ''
        }
        {
          options ? (
            <React.Fragment>
              <p>
                Now on <b>{options.currentCourtName}</b>, {weekdaysReverse[options.currentWeekDay]} {formatCourtTime(options.currentTime)}.
                {' '}Only courts at the same facility, in the same price band and on sale are offered.
                {' '}The move starts {options.minDaysAhead} days from today at the earliest ({options.earliestMoveDate}).
                {' '}The subscription is not touched.
              </p>
              {
                courts.length === 0 ? <p><b>No court can take this purchase right now.</b></p> : (
                  <React.Fragment>
                    <label htmlFor='change-court-court'>New court</label>
                    <br />
                    <select
                      id='change-court-court'
                      value={courtId}
                      onChange={e => this.selectCourt(e.target.value)}
                    >
                      <option value=''>— choose a court —</option>
                      {courts.map(c => (
                        <option key={c.id} value={c.id} disabled={!c.firstAvailableDate}>
                          {`${c.courtName} · ${weekdaysReverse[c.weekDay]} ${formatCourtTime(c.time)} · `}
                          {c.firstAvailableDate
                            ? `free from ${c.firstAvailableDate} (${c.availableSlots} free, ${c.unavailableSlots} taken)`
                            : 'no free slot'}
                        </option>
                      ))}
                    </select>
                    <br />
                    <br />
                    <label htmlFor='change-court-from'>Move from (Copenhagen day)</label>
                    <br />
                    <input
                      id='change-court-from'
                      type='date'
                      min={options.earliestMoveDate}
                      value={moveFrom}
                      disabled={!courtId}
                      onChange={e => this.setState({ moveFrom: e.target.value, preview: null })}
                    />
                  </React.Fragment>
                )
              }
            </React.Fragment>
          ) : ''
        }
        {error ? <p style={{ color: '#c00' }}>{error}</p> : ''}
        {
          preview ? (
            <div>
              <p>
                <b>Old court</b>: {preview.slotsToCancel.length} slot(s) cancelled here and at the venue
                {preview.slotsToCancel.length ? ` (${dateList(preview.slotsToCancel)})` : ''}.
              </p>
              <p>
                <b>{preview.newCourtName}</b>: {preview.slotsToBook.length} slot(s) booked
                {preview.slotsToBook.length ? ` (${dateList(preview.slotsToBook)})` : ''}.
              </p>
              {
                preview.slotsUnavailable.length ? (
                  <p>
                    <b>Not free at the venue, not booked</b>: {dateList(preview.slotsUnavailable)}.
                  </p>
                ) : ''
              }
              <p><i>{COPENHAGEN_TIME_NOTE}</i></p>
            </div>
          ) : ''
        }
        <br />
        {
          preview ? (
            <ButtonOBSOLETED
              buttonColor='danger'
              size='small'
              onClick={() => this.run(false)}
              disabled={busy}
              buttonContent='Change court'
              ariaLabel='change court'
            >
              Change court
            </ButtonOBSOLETED>
          ) : (
            <ButtonOBSOLETED
              buttonColor='primary'
              size='small'
              onClick={() => this.run(true)}
              disabled={busy || !courtId || !moveFrom}
              buttonContent='Preview'
              ariaLabel='preview the court change'
            >
              Preview
            </ButtonOBSOLETED>
          )
        }
        <br />
        <ButtonOBSOLETED
          buttonColor='gray'
          size='small'
          onClick={onClose}
          disabled={busy}
          buttonContent='Back'
          ariaLabel='return'
        >
          Back
        </ButtonOBSOLETED>
      </div>
    );
  }
}

ChangeCourtDialog.propTypes = {
  purchaseId: PropTypes.string.isRequired,
  api: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired
};

const ExternalPurchaseStatus = [
  'Undefined',
  'Booked',
  'Cancelled',
  'Pending',
  'PendingCancellation'
];

const SeasonalCourtSlotStatus = [
  'Undefined',
  'NotIntegrated',
  'NotFound',
  'NotAvailable',
  'Found',
  'Reserved',
  'CancelledByUser'
];

// Mirrors the backend enum SeasonalCourtSlotPurchaseStatus (Undefined=0, Purchased=1, Cancelled=2).
const SeasonalCourtSlotPurchaseStatus = [
  'Undefined',
  'Purchased',
  'Cancelled'
];

class SeasonalCourtPurchaseInternal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      purchaseData: null
    };
  }

  async componentDidMount() {
    await this.load();
  }

  async componentDidUpdate(prevProps) {
    // "Change court" navigates to the new purchase, which reuses this component.
    if (prevProps.match.params.seasonalCourtPurchaseId !== this.props.match.params.seasonalCourtPurchaseId) {
      this.setState({ purchaseData: null });
      await this.load();
    }
  }

  async load() {
    const { match: { params: { seasonalCourtPurchaseId } } } = this.props;
    const { api: { seasonalcourts: { getPurchase } } } = this.props;

    try {
      const purchaseData = await getPurchase(seasonalCourtPurchaseId);
      this.setState({ purchaseData });
    }
    catch (e) {
      this.setState({ errorMessage: new Message('error', 'Unable to get data') });
    }
  }

  render() {
    const { match: { params: { seasonalCourtPurchaseId } }, history, api } = this.props;
    const { purchaseData, errorMessage, isConfirming, preview, cancelFrom } = this.state;

    let mappedData = null;

    if (purchaseData) {
      let {
        id,
        seasonalCourtId,
        userId,
        userName,
        status,
        terminatedAt,
        purchaseId,
        subscriptionId
      } = purchaseData;

      mappedData = {
        Id: <DisplayGuid>{id}</DisplayGuid>,
        userId: <DisplayGuid baseAnchor='/user'>{userId}</DisplayGuid>,
        userName,
        SeasonalCourtId: <DisplayGuid baseAnchor='/seasonalcourt'>{seasonalCourtId}</DisplayGuid>,
        'Purchase': purchaseId ? <DisplayGuid baseAnchor='/purchase'>{purchaseId}</DisplayGuid> : 'N/A',
        'Subscription': subscriptionId ? <DisplayGuid baseAnchor='/subscription'>{subscriptionId}</DisplayGuid> : 'N/A',
        Status: status ?? '',
        TerminatedAt: terminatedAt ? formatCopenhagen(terminatedAt, { dateOnly: true }) : ''
      };
    }

    return (
      <LayoutContext.Consumer>
        {() => (
          <React.Fragment>
            {
              isConfirming === 'changeCourt' && purchaseData ? (
                <AriaModal
                  titleText='Change court'
                  mounted
                  verticallyCenter={false}
                  focusDialog
                  getApplicationNode={() => document.getElementById('app')}
                  dialogClass='dialog-size-normal'
                  scrollDisabled
                >
                  <ChangeCourtDialog
                    purchaseId={purchaseData.id}
                    api={api}
                    onClose={() => this.setState({ isConfirming: false })}
                    onDone={newId => {
                      this.setState({ isConfirming: false });
                      history.push(`/seasonalcourtpurchase/${newId}`);
                    }}
                  />
                </AriaModal>
              ) : ''
            }
            {
              isConfirming && isConfirming !== 'changeCourt' ? (
                // TODO: extract modal
                <AriaModal
                  titleText='Edit Field'
                  mounted
                  verticallyCenter={false}
                  focusDialog
                  getApplicationNode={() => document.getElementById('app')}
                  dialogClass='dialog-size-normal'
                  scrollDisabled
                >
                  <h3>
                    Are you sure you want to {isConfirming} ?
                  </h3>

                  {
                    isConfirming !== 'cancel' ? '' : (
                      <div>
                        <p>
                          Slots from this day on are cancelled, here and at the venue. Earlier slots are kept.
                          {' '}The purchase is marked as terminating on this day; the subscription is not touched.
                        </p>
                        <label htmlFor='cancel-slots-from'>Cancel slots from (Copenhagen day)</label>
                        <br />
                        <input
                          id='cancel-slots-from'
                          type='date'
                          value={cancelFrom || ''}
                          onChange={e => this.setState({ cancelFrom: e.target.value })}
                        />
                        <p>
                          <i>
                            Default: {purchaseData?.defaultCancelSlotsFrom
                              ? formatCopenhagen(purchaseData.defaultCancelSlotsFrom, { dateOnly: true })
                              : 'first day of next month'}
                            {' '}(the subscription's end date if it has one, otherwise the first of next month).
                          </i>
                        </p>
                      </div>
                    )
                  }

                  {
                    isConfirming != 'email' ? '' : 
                    !preview ? 'loading' : 
                      <div className="content" dangerouslySetInnerHTML={{__html: preview}}></div> 
                  }

                  <ButtonOBSOLETED
                    buttonColor='danger'
                    size='small'
                    onClick={async () => {
                      const {
                        api: {
                          seasonalcourts: {
                            book,
                            cancel,
                            integrate,
                            getPurchase,
                            sendConfirmationEmail
                          }
                        }
                      } = this.props;

                      this.setState({
                        isConfirming: false,
                        purchaseData: null
                      });

                      try {
                        switch (isConfirming) {
                          case 'book':
                            await book(purchaseData.id);
                            break;
                          case 'email':
                            await sendConfirmationEmail(purchaseData.id, false);
                            break;
                          case 'cancel':
                            await cancel(purchaseData.id, cancelFrom);
                            break;
                          case 'integrate':
                            await integrate(purchaseData.seasonalCourtId);
                            break;
                          default: alert('unexpected error: action not found');
                        }
                      }
                      catch (e) {
                        this.setState({
                          errorMessage: new Message('error', `Unable to ${isConfirming} purchase: ${errorText(e)}`)
                        });
                      }

                      const newSeasonalCourtPurchaseData = await getPurchase(seasonalCourtPurchaseId);

                      this.setState({
                        isConfirming: false,
                        purchaseData: newSeasonalCourtPurchaseData
                      });
                    }}
                    disabled={isConfirming === 'cancel' && !cancelFrom}
                    buttonContent={`${isConfirming} purchase`}
                    ariaLabel={`${isConfirming} purchase`}
                  >
                    Proceed
                  </ButtonOBSOLETED>
                  <br />
                  <ButtonOBSOLETED
                    buttonColor='gray'
                    size='small'
                    onClick={() => {
                      this.setState({ isConfirming: false });
                    }}
                    disabled={false}
                    buttonContent='Back'
                    ariaLabel='return'
                  >
                    Back
                  </ButtonOBSOLETED>
                </AriaModal>
              ) : ''
            }
            <Notification newMessage={errorMessage} />

            <div className='content-with-right-pane-delimiter'>
              <PageTop
                documentTitle='SeasonalCourtPurchase details'
                documentDescription='SeasonalCourtPurchase details'
              />

              <h2 className='main-page-header main-page-header-with-subheader'>
                SeasonalCourtPurchase
              </h2>
              <h5 className='main-page-subheader'>{seasonalCourtPurchaseId}</h5>
              <br />

              {
                mappedData ? (
                  <React.Fragment>
                    <DataDefinitionList
                      listData={mappedData}
                      classNames={['detail-page-list']}
                    />
                  </React.Fragment>
                ) : <Loader hasLoaded={false}>Loading...</Loader>
              }
            <p><i>{COPENHAGEN_TIME_NOTE}</i></p>
            <article className='data-table-wrap'>
              <table className='data-lists component-data-table cell-content-align-top'>
                <thead>
                  <tr>
                      <td>
                        Id
                      </td>
                      <td>
                        Start <br />
                        (Copenhagen)
                      </td>
                      <td>
                        End <br />
                        (Copenhagen)
                      </td>
                      <td>
                        Purchase  <br />
                        Slot Status
                      </td>
                      <td>
                        Court <br />
                        Slot Status
                      </td>
                      <td>
                        External <br/> Purchase Id
                      </td>
                      <td>
                        External <br />Status
                      </td>
                      <td>
                        References
                      </td>
                  </tr>
                </thead>
                <tbody>
                {
                purchaseData && purchaseData.slots ? purchaseData.slots.map(({ 
                    externalPurchaseId, 
                    seasonalCourtSlotPurchaseId,
                    status,
                    externalStatus, 
                    start,
                    end, 
                    slotStatus, 
                    references}) => { 
                      return (
                    <tr key={seasonalCourtSlotPurchaseId}>
                      <td>
                        {seasonalCourtSlotPurchaseId}
                      </td>
                      <td>
                        {formatCopenhagen(start)}
                      </td>
                      <td>
                        {formatCopenhagen(end)}
                      </td>
                      <td>
                        {status ? `${SeasonalCourtSlotPurchaseStatus[status]}[${status}]` : ''}
                      </td>
                      <td>
                        {slotStatus ? `${SeasonalCourtSlotStatus[slotStatus]}[${slotStatus}]` : ''}
                      </td>
                      <td>
                        {externalPurchaseId ? <DisplayGuid baseAnchor='/externalpurchase'>{externalPurchaseId}</DisplayGuid> : 'none'}
                      </td>
                      <td>
                       {externalStatus ? `${ExternalPurchaseStatus[externalStatus]}[${externalStatus}]` : ''}
                      </td>
                      <td>
                        {references || 'none'}
                      </td>
                    </tr>)}) : ''
                }
                </tbody>
              </table>
              </article>
            </div>

            <DetailsRightBar>
              <Divider>Links</Divider>
              {purchaseData 
              ? (<Link to={`/confirmationEmail?seasonalCourtPurchaseId=${purchaseData.id}`}>Emails</Link>)
              : ""}
              <Divider>Actions</Divider>
              <Action action={() => {
                this.setState({ isConfirming: 'book' });
              }}
              >
                Book
              </Action>
              <Action action={() => {
                this.setState({
                  isConfirming: 'cancel',
                  cancelFrom: purchaseData?.defaultCancelSlotsFrom
                    ? formatCopenhagen(purchaseData.defaultCancelSlotsFrom, { dateOnly: true, fallback: '' })
                    : ''
                });
              }}
              >
                Cancel
              </Action>
              <Action action={() => {
                this.setState({ isConfirming: 'changeCourt' });
              }}
              >
                Change court
              </Action>
              <Action action={() => {
                this.setState({ isConfirming: 'integrate' });
              }}
              >
                Re-integrate
              </Action>
              <Action action={async () => {
                const {
                  api: {
                    seasonalcourts: {
                      sendConfirmationEmail
                    }
                  }
                } = this.props;
                this.setState({ isConfirming: 'email', preview: false });
                var result = await sendConfirmationEmail(purchaseData.id, true);
                this.setState({ preview: result });
              }}
              >
                Send Confirmation Email
              </Action>
            </DetailsRightBar>
          </React.Fragment>
        )}
      </LayoutContext.Consumer>
    );
  }
}

const SeasonalCourtPurchase = withRouter(withApi(connect(({ user }) =>
  ({ isLoggedIn: !!user }), {})(SeasonalCourtPurchaseInternal)));

SeasonalCourtPurchase.requiresLogin = true;
SeasonalCourtPurchase.propTypes = { isLoggedIn: PropTypes.bool };

export default SeasonalCourtPurchase;